import React from "react";
import { absolutiseApiUrl } from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";

// Palette used to colour the initials fallback. Chosen for good contrast on white text.
const COLORS = [
  "bg-sky-600", "bg-violet-600", "bg-emerald-600", "bg-amber-600",
  "bg-pink-600", "bg-indigo-600", "bg-teal-600", "bg-rose-600",
  "bg-cyan-600", "bg-fuchsia-600", "bg-lime-600", "bg-orange-600",
];

function hashColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SIZES = {
  xs: { box: "w-6 h-6 text-[10px]" },
  sm: { box: "w-8 h-8 text-xs" },
  md: { box: "w-10 h-10 text-sm" },
  lg: { box: "w-14 h-14 text-lg" },
  xl: { box: "w-20 h-20 text-2xl" },
  "2xl": { box: "w-24 h-24 text-3xl" },
};

/**
 * PersonAvatar — one avatar to rule them all.
 *
 * Props:
 *  - person: { name, preferred_name, full_name, photo_url, photo_tile_url, photo_thumb_url }
 *  - name:   fallback name string (overrides person.name)
 *  - src:    override photo URL directly
 *  - size:   xs | sm | md | lg | xl | 2xl (default md)
 *  - ring:   boolean — adds a subtle white ring (for stacked avatars)
 *  - className, testid
 */
export default function PersonAvatar({ person, name, src, size = "md", ring = false, authed = false, className = "", testid }) {
  const displayName = name || person?.preferred_name || person?.name || person?.full_name || "";
  const cfg = SIZES[size] || SIZES.md;

  let url = src;
  if (!url && person) {
    if (size === "xl" || size === "2xl" || size === "lg") {
      url = person.photo_url || person.photo_tile_url || person.photo_thumb_url;
    } else if (size === "xs" || size === "sm") {
      url = person.photo_thumb_url || person.photo_tile_url || person.photo_url;
    } else {
      url = person.photo_tile_url || person.photo_thumb_url || person.photo_url;
    }
  }
  const fullUrl = absolutiseApiUrl(url);
  const ringCls = ring ? "ring-2 ring-white dark:ring-slate-900" : "";
  const bgCls = fullUrl ? "bg-slate-200" : `${hashColor(displayName)} text-white font-semibold`;

  return (
    <div
      className={`inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${cfg.box} ${bgCls} ${ringCls} ${className}`}
      title={displayName}
      data-testid={testid}
      aria-label={displayName || "Avatar"}
    >
      {fullUrl ? (
        authed ? (
          <AuthedImage src={url} alt="" className="w-full h-full object-cover" testid={testid ? `${testid}-img` : undefined} />
        ) : (
          <img src={fullUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        )
      ) : (
        <span aria-hidden="true">{initials(displayName)}</span>
      )}
    </div>
  );
}
